import { useCallback, useState } from "react";
import { useSession } from "../context/SessionContext";
import { useApi } from "./useApi";
import { patientService } from "../services/patients";
import { Surgery } from "../types/patient.types";

// Pacientes e pedidos do médico logado, só da especialidade atual dele.
export function useOrdersApi() {
  const { surgeries, setSurgeries, metadata } = useSession();
  const doctorEmail = metadata.email;
  const specialty = metadata.especialidade;
  const [patients, setPatients] = useState<string[]>([]);
  const [ordersCount, setOrdersCount] = useState<Record<string, number>>({});

  const patientsApi = useApi<{ unique_patients: string[]; patient_orders_count?: Record<string, number>; }>();
  const surgeriesApi = useApi<{ surgeries: Surgery[] }>();

  const { request: patientsRequest, apiFetch: patientsFetch } = patientsApi;
  const { request: surgeriesRequest, apiFetch: surgeriesFetch } = surgeriesApi;

  const fetchPatientsByDoctor = useCallback(async () => {
    if (!doctorEmail) return;

    const data = await patientsRequest(
      () => patientService.getPatients(patientsFetch, doctorEmail, specialty)
    );

    if (data) {
      const sortedPatientNames = [...data.unique_patients].sort((a, b) => a.localeCompare(b));
      setPatients(sortedPatientNames);

      if (data.patient_orders_count) {
        setOrdersCount(data.patient_orders_count);
      }

      return sortedPatientNames;
    }
  }, [doctorEmail, specialty, patientsRequest, patientsFetch]);

  const fetchSurgeriesByPatient = useCallback(
    async (patientName: string) => {
      if (!doctorEmail) return;

      const data = await surgeriesRequest(
        () => patientService.getSurgeries(surgeriesFetch, doctorEmail, patientName, specialty)
      );

      if (data) {
        setSurgeries(data.surgeries);
        return data.surgeries;
      }
    },
    [doctorEmail, specialty, surgeriesRequest, surgeriesFetch, setSurgeries]
  );

  return {
    patients: patients,
    ordersCount: ordersCount,
    surgeries: surgeries,
    // Antes da primeira resposta também é "carregando": senão o vazio pisca antes do skeleton.
    loadingPatients: patientsApi.loading || (!patientsApi.data && !patientsApi.error),
    patientsFailed: Boolean(patientsApi.error),
    loadingSurgeries: surgeriesApi.loading,
    fetchPatientsByDoctor,
    fetchSurgeriesByPatient,
  };
}
